/* eslint-disable no-console */
import { EventEmitter } from 'events';
import * as signalR from '@microsoft/signalr';

const OPTIONS_KEY = '__channel_calls__';

type BasicCallback = (...args: any[]) => Promise<void>;
type TypedCallback = <T>(...args: any[]) => Promise<T>;

export interface SignalROptions
{
    endpoint: string;
}

class Channel extends EventEmitter
{
    private connection: signalR.HubConnection = null;
    private started = false;
    private name: string;

    public constructor(name: string, options: SignalROptions)
    {
        super();

        this.name = name;
        this.connection = new signalR.HubConnectionBuilder()
            .configureLogging(signalR.LogLevel.Error)
            .withUrl(`${options.endpoint}/${name}`, {
                transport: signalR.HttpTransportType.WebSockets,
                skipNegotiation: true // This setting can't be enabled when using the Azure SignalR Service.
            })
            .build();

        this.connection
            .onclose(() =>
            {
                console.log(`SignalR: Connection closed (${this.name}).`);

                setTimeout(() =>
                {
                    if (this.started)
                    {
                        this.start();
                    }
                },
                5000);
            });
    }

    public start()
    {
        this.connection
            .start()
            .then(() =>
            {
                this.emit('started');
                console.log(`SignalR: Connection started (${this.name}).`);
            })
            .catch((err: any) =>
            {
                console.log(`Error while establishing connection (${this.name}).`, err);

                setTimeout(() =>
                {
                    if (this.started)
                    {
                        this.start();
                    }
                },
                15000);
            });

        this.started = true;

        return this;
    }

    public stop()
    {
        this.connection.stop();
        this.started = false;

        return this;
    }

    public register(type: string, method: string, callback: any)
    {
        if (type == 'Listen')
        {
            this.proxy.on(method, callback);
        }
    }

    public unregister(type: string, method: string, callback: any)
    {
        if (type == 'Listen')
        {
            this.proxy.off(method, callback);
        }
    }

    public invoke(method: string, ...args: any[])
    {
        return this.proxy.invoke(method, ...args).then((response) =>
        {
            return new Promise((resolve) =>
            {
                resolve(convertDates(response));
            });
        });
    }

    private get proxy()
    {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const channel = this;

        return new Proxy(this.connection, {
            get(connection: any, property: string)
            {
                return function (...args: any[])
                {
                    if (connection.state == signalR.HubConnectionState.Connected)
                    {
                        return connection[property](...args);
                    }

                    return new Promise((resolve) =>
                    {
                        return channel.once('started', () =>
                        {
                            const result = connection[property](...args);

                            if (result && result.then)
                            {
                                result.then((data: any) => resolve(data));
                            }
                            else if (result)
                            {
                                resolve(result);
                            }
                            else
                            {
                                resolve(null);
                            }
                        });
                    });
                };
            }
        });
    }

    // Metoda do strumieniowania danych
    public stream(method: string, callback: any, ...args: any[])
    {
        if (this.connection.state === signalR.HubConnectionState.Connected)
        {
            this.proxy.stream(method, ...args).subscribe({
                next: (item: any) => callback(item),
                complete: () => console.log('Streaming complete'),
                error: (err: any) => console.log('Streaming error:', err)
            });
        }
    }
}

class SignalRHelper
{
    private options: SignalROptions = null;
    private channels: Record<string, Channel> = {};
    private components: Record<string, any[]> = {};

    public constructor(options: SignalROptions)
    {
        this.options = options;
    }

    public channel(name: string)
    {
        return this.channels[name] || this.connect(name);
    }

    public register(component: any)
    {
        this.components = this.components || {};

        const calls = component.$options[OPTIONS_KEY] || [];

        calls.forEach((entry: string) =>
        {
            const [type, channel, method, callback] = entry.split('.');

            this.channel(channel).register(type, method, component[callback]);
        });
    }

    public unregister(component: any)
    {
        this.components = this.components || {};

        const calls = component.$options[OPTIONS_KEY] || [];

        calls.forEach((entry: string) =>
        {
            const [type, channel, method, callback] = entry.split('.');

            this.channel(channel).unregister(type, method, component[callback]);
        });
    }

    private connect(name: string)
    {
        return (this.channels[name] = new Channel(name, this.options).start());
    }

    private disconnect(name: string)
    {
        if (this.channels[name])
        {
            this.channels[name].stop();
            delete this.channels[name];
        }
    }
}

export function channel(channelName: string)
{
    const { $channels } = useNuxtApp();

    return {
        on: new Proxy($channels, {
            set(channels, methodName, methodBody)
            {
                const channel = channels.channel(channelName);
                const method = (...args: any[]) =>
                {
                    Reflect.apply(methodBody, null, [...args]);
                };

                channel.register('Listen', methodName.toString(), method);

                onUnmounted(() =>
                {
                    channel.unregister('Listen', methodName.toString(), method);
                });

                return true;
            }
        }) as unknown as Record<string, BasicCallback>,
        invoke: new Proxy($channels, {
            get(channels, method)
            {
                const channel = channels.channel(channelName);

                return (...args: any[]) => channel.invoke(method.toString(), ...args);
            }
        }) as unknown as Record<string, BasicCallback | TypedCallback>,
        // Nowe proxy dla strumieniowych wywołań
        stream: new Proxy($channels, {
            get(channels, method)
            {
                const channel = channels.channel(channelName);

                return (callback: (...args: any[]) => void, ...args: any[]) => channel.stream(method.toString(), callback, ...args);
            }
        }) as unknown as Record<string, (callback: (...args: any[]) => void, ...args: any[]) => void>
    };
}

export default defineNuxtPlugin({
    name: 'signalr',
    dependsOn: [],
    async setup(nuxtApp)
    {
        const { $config } = useNuxtApp();
        const endpoint = `${$config.public.appUrl}/hubs` as string;
        const helper = new SignalRHelper({
            endpoint: endpoint
        });

        nuxtApp.vueApp.mixin({
            async created()
            {
                helper.register(this);
            },
            async unmounted()
            {
                helper.unregister(this);
            }
        });

        return {
            provide: {
                channels: helper
            }
        };
    }
});

declare module '@vue/runtime-core'
{
    interface ComponentCustomProperties
    {
        $channels: SignalRHelper;
    }
}
